import { useEffect, useState } from 'react'

const AppIcon = ({ icon, iconClass, name, appName, onOpenApp, color }) => {
  const handleRightClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const contextMenuEvent = new CustomEvent('showContextMenu', {
      detail: { event: e, type: 'start-menu-icon', data: { appName } }
    })
    document.dispatchEvent(contextMenuEvent)
  }

  return (
    <div 
      className="start-menu-icon flex flex-col items-center p-2 rounded-lg hover:bg-white/10" 
      onClick={() => onOpenApp(appName)}
      onContextMenu={handleRightClick}
    >
      <div className={`${iconClass} p-3 rounded-xl mb-1`}>
        <i className={`${icon} text-xl`} style={{color}}></i>
      </div>
      <span className="text-xs">{name}</span>
    </div>
  )
}

const StartMenu = ({ onOpenApp, onClose, onShutdown }) => {
  const [theme, setTheme] = useState(localStorage.getItem('pp_icon_theme') || 'neon')
  const [userColor, setUserColor] = useState(localStorage.getItem('pp_icon_user_color') || '#f472b6')
  useEffect(()=>{const handler=(e)=>{if(e.detail?.id) setTheme(e.detail.id); if(e.detail?.color) setUserColor(e.detail.color)};window.addEventListener('iconThemeChange',handler);return()=>window.removeEventListener('iconThemeChange',handler)},[])
  const palettes = {
    pinkPixel: ['#f472b6', '#ec4899', '#db2777', '#be185d', '#9d174d'],
    cloudwerx: ['#38bdf8', '#0ea5e9', '#0284c7', '#0369a1', '#075985'],
    monotone: ['#ffffff'],
    neon: ['#ff00ff', '#00eaff', '#39ff14', '#fffb00', '#ff007f'],
    pastel: ['#fbcfe8', '#bfdbfe', '#fde68a', '#c7d2fe', '#bbf7d0'],
    rainbow: ['#ef4444', '#f59e0b', '#eab308', '#10b981', '#3b82f6', '#6366f1', '#db2777'],
    userColor: [userColor]
  }
  const palette = palettes[theme] || palettes.neon
  const colorForIndex = (i) => palette[i % palette.length]

  const [searchQuery, setSearchQuery] = useState('')

  const frequentApps = [
    { icon: 'fas fa-folder text-blue-400', iconClass: 'bg-blue-500/20', name: 'Explorer', appName: 'explorer' },
    { icon: 'fas fa-globe text-green-400', iconClass: 'bg-green-500/20', name: 'Browser', appName: 'browser' },
    { icon: 'fas fa-shopping-bag text-purple-400', iconClass: 'bg-purple-500/20', name: 'Store', appName: 'store' },
    { icon: 'fas fa-calculator text-orange-400', iconClass: 'bg-orange-500/20', name: 'Calculator', appName: 'calculator' },
    { icon: 'fas fa-file-text text-cyan-400', iconClass: 'bg-cyan-500/20', name: 'Notepad', appName: 'notepad' },
    { icon: 'fas fa-images text-pink-400', iconClass: 'bg-pink-500/20', name: 'Photos', appName: 'photos' },
    { icon: 'fas fa-th text-blue-400', iconClass: 'bg-gradient-to-br from-blue-500/20 to-purple-500/20', name: 'Apps', appName: 'apps' },
    { icon: 'fab fa-spotify text-green-400', iconClass: 'bg-green-500/20', name: 'Spotify', appName: 'spotify' },
    { icon: 'fas fa-gamepad text-pink-400', iconClass: 'bg-gradient-to-br from-pink-500/20 to-purple-500/20', name: 'Games', appName: 'games' },
    { icon: 'fas fa-calendar text-green-400', iconClass: 'bg-green-500/20', name: 'Calendar', appName: 'calendar' },
    { icon: 'fas fa-cog text-blue-400', iconClass: 'bg-blue-500/20', name: 'Settings', appName: 'settings' },
    { icon: 'fas fa-envelope text-yellow-400', iconClass: 'bg-yellow-500/20', name: 'Mail', appName: 'mail' },
    { icon: 'fab fa-youtube text-red-500', iconClass: 'bg-red-500/20', name: 'YouTube', appName: 'youtube' },
    { icon: 'fas fa-cloud-sun text-sky-400', iconClass: 'bg-sky-500/20', name: 'Weather', appName: 'weather' },
    { icon: 'fas fa-palette text-pink-400', iconClass: 'bg-gradient-to-br from-pink-500/20 to-purple-500/20', name: 'Color Picker', appName: 'colorpicker' },
    { icon: 'fas fa-newspaper text-blue-400', iconClass: 'bg-blue-500/20', name: 'News', appName: 'news' },
    { icon: 'fas fa-terminal text-green-400', iconClass: 'bg-green-500/20', name: 'Terminal', appName: 'terminal' },
    { icon: 'fas fa-table-tennis-paddle-ball text-amber-400', iconClass: 'bg-amber-500/20', name: 'Arkanoid', appName: 'arkanoid' },
    { icon: 'fas fa-chess text-yellow-400', iconClass: 'bg-yellow-500/20', name: 'Chess', appName: 'chess' },
    { icon: 'fas fa-space-shuttle text-blue-400', iconClass: 'bg-blue-500/20', name: 'Space Invaders', appName: 'spaceinvaders' },
  ]

  const filteredApps = frequentApps.filter(app =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.appName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="start-menu absolute bottom-20 left-1/2 transform -translate-x-1/2 glass-effect rounded-2xl p-4 w-[92vw] sm:w-[500px] max-w-[500px] max-h-[80vh] flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <input 
          type="text" 
          placeholder="Search applications..." 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-white/10 rounded-full px-4 py-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-white/40"
        />
      </div>      
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 overflow-y-auto max-h-[50vh] p-1">
        {filteredApps.length === 0 ? (
          <div className="col-span-full py-8 text-center text-sm text-white/50">
            No applications found matching &quot;{searchQuery}&quot;
          </div>
        ) : (
          filteredApps.map((app, index) => (
            <AppIcon 
              key={app.appName}
              icon={`${app.icon.split(' ').slice(0,2).join(' ')}`} 
              iconClass={app.iconClass}
              name={app.name}
              appName={app.appName}
              onOpenApp={(appName) => {
                onOpenApp(appName)
                onClose()
              }}
              color={colorForIndex(index)}
            />
          ))
        )}
      </div>
      
      <div className="mt-6 pt-4 border-t border-white/10">
        <div 
          className="flex items-center p-2 rounded-lg hover:bg-white/10 cursor-pointer transition-colors"
          onClick={() => {
            onShutdown?.();
            onClose();
          }}
        >
          <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center mr-3">
            <i className="fas fa-power-off text-blue-400 text-sm"></i>
          </div>
          <span className="text-sm">Shut down</span>
        </div>
      </div>
    </div>
  )
}

export default StartMenu